import React from 'react';
import ReactCrop from 'react-image-crop';
import { Sparkles, StarIcon } from 'lucide-react';
import { X } from 'lucide-react';
import SafeZoneOverlay from './SafeZoneOverlay';
import AnnotationOverlay from './AnnotationOverlay';
import Spinner from './Spinner';
import ImageLabel from './ImageLabel';

import { useImageRef } from '../contexts/ImageRefContext';
import { useCrop } from '../contexts/CropContext';
import { useRegionEdit } from '../contexts/RegionEditContext';
import { useFeedback } from '../contexts/FeedbackContext';
import { useEditor } from '../contexts/EditorContext';

interface EditorCanvasProps {
  currentPictureId: string | undefined;
}

const EditorCanvas: React.FC<EditorCanvasProps> = ({ currentPictureId }) => {
  const { imgRef, imgSize, setImgRef, renderedSize } = useImageRef();
  const { crop, setCrop, setCompletedCrop, aspect } = useCrop();
  const { isRegionSelectMode, regionCrop, setRegionCrop, setRegionCompletedCrop, regionAspect } = useRegionEdit();
  const {
    isAnnotationMode, pendingAnnotation, highlightedCommentId, comments,
  } = useFeedback();
  const {
    selectedImageInGroup, setSelectedImageInGroup,
    isLoading, loadingMessage,
    editHotspot, activeTab, isComparing,
    imagesLoading, currentImage, currentImageGroup, originalImage,
    hoveredCombo,
    handleImageClick, handleToggleStar,
  } = useEditor();

  // Image display components - only defined when currentImage exists (PICTURE MODE)
  const imageDisplay = (
    <div className="image-display-container relative w-full h-full flex items-center justify-center">
      {/* Show multiple images in a grid if the current group has more than one image AND no image is selected for full view */}
      {currentImageGroup && currentImageGroup.images.length > 1 && selectedImageInGroup === -1 ? (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-4 min-h-0">
          {/* Indicator showing this is a batch of images */}
          <div className="absolute top-4 left-4 bg-blue-500/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg z-20 flex items-center gap-2">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            {currentImageGroup.images.length} images
          </div>

          {/* Instruction banner */}
          <div className="absolute top-4 right-4 bg-purple-500/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg z-20 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Click an image to select it for editing
          </div>

          {/* Grid of images */}
          <div className={`grid gap-3 w-full h-full min-h-0 ${
            currentImageGroup.images.length === 2 ? 'grid-cols-2' :
            currentImageGroup.images.length === 3 ? 'grid-cols-3' :
            currentImageGroup.images.length === 4 ? 'grid-cols-2 grid-rows-2' :
            currentImageGroup.images.length <= 6 ? 'grid-cols-3 grid-rows-2' :
            'grid-cols-4'
          }`}>
            {currentImageGroup.images.map((img, idx) => (
              <div
                key={img.picture_id}
                className="relative group cursor-pointer min-h-0 overflow-hidden"
                onClick={() => setSelectedImageInGroup(idx)}
              >
                <img
                  src={img.output_url}
                  alt={`Original (${idx + 1} / ${currentImageGroup.images.length})`}
                  className="w-full h-full object-contain rounded-lg border-2 border-gray-700/50 group-hover:border-blue-500/70 group-hover:shadow-xl group-hover:shadow-blue-500/30 transition-all"
                />

                {/* Hover overlay with "Select" text */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <div className="bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Select for Editing
                  </div>
                </div>

                {/* Star button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleStar(img);
                  }}
                  className="absolute top-2 left-2 z-10 p-1 rounded-full bg-black/50 backdrop-blur-sm transition-colors hover:bg-black/70"
                  aria-label={img.starred ? 'Unstar image' : 'Star image'}
                  title={img.starred ? 'Remove star' : 'Star this image'}
                >
                  <StarIcon
                    className={`w-4 h-4 ${img.starred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                  />
                </button>

                {img.meta?.label && (
                  <ImageLabel
                    label={img.meta.label}
                    platformLabel={img.meta.platformLabel || ''}
                    aspectRatio={img.meta.aspectRatio || ''}
                  />
                )}

                <div className="absolute top-2 right-2 z-10 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded text-xs font-semibold">
                  #{idx + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : currentImageGroup && currentImageGroup.images.length > 1 && selectedImageInGroup >= 0 && currentImage ? (
        /* Single image from group is selected - show with close button */
        <>
          {/* Close button to return to grid view */}
          <button
            onClick={() => setSelectedImageInGroup(-1)}
            className="absolute top-4 right-4 z-20 p-2 bg-red-500/90 hover:bg-red-600 backdrop-blur-sm text-white rounded-full transition-all duration-200 hover:scale-110 active:scale-95 shadow-lg group"
            aria-label="Return to grid view"
          >
            <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
          </button>

          {/* Selected image indicator */}
          <div className="absolute top-4 left-4 bg-blue-500/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg z-20 flex items-center gap-2">
            <span className="w-2 h-2 bg-white rounded-full"></span>
            Image {selectedImageInGroup + 1} of {currentImageGroup.images.length}
          </div>

          {/* Star button */}
          <button
            onClick={() => handleToggleStar(currentImage)}
            className="absolute top-14 left-4 z-20 p-1.5 rounded-full bg-black/50 backdrop-blur-sm transition-colors hover:bg-black/70"
            aria-label={currentImage.starred ? 'Unstar image' : 'Star image'}
            title={currentImage.starred ? 'Remove star' : 'Star this image'}
          >
            <StarIcon
              className={`w-5 h-5 ${currentImage.starred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
            />
          </button>

          {/* Base image is the original, always at the bottom */}
          {originalImage && (
              <img
                  key='original-image'
                  src={originalImage.output_url}
                  alt="Original"
                  className={`w-full h-auto object-contain max-h-[calc(100vh-200px)] rounded-xl pointer-events-none transition-opacity duration-200 ease-in-out ${isComparing ? 'opacity-100' : 'opacity-0'}`}
              />
          )}
          {/* The selected image — wrapped in ReactCrop when region mode is active */}
          {isRegionSelectMode && (activeTab === 'Editing' || activeTab === 'Translating') ? (
            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-in-out ${isComparing ? 'opacity-0' : 'opacity-100'}`}>
              <ReactCrop
                crop={regionCrop}
                onChange={c => setRegionCrop(c)}
                onComplete={c => setRegionCompletedCrop(c)}
                aspect={regionAspect}
                style={{ maxHeight: 'calc(100vh - 200px)', maxWidth: '100%' }}
              >
                <img
                  ref={setImgRef}
                  key={currentImage.output_url}
                  src={currentImage.output_url}
                  alt="Current"
                  className="block max-w-full max-h-[calc(100vh-200px)] rounded-xl select-none"
                  draggable={false}
                />
              </ReactCrop>
            </div>
          ) : (
            <img
                ref={setImgRef}
                key={currentImage.output_url}
                src={currentImage.output_url}
                alt="Current"
                onMouseDown={handleImageClick}
                className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-auto object-contain max-h-[calc(100vh-200px)] rounded-xl transition-opacity duration-200 ease-in-out ${isComparing ? 'opacity-0' : 'opacity-100'} ${isAnnotationMode ? 'cursor-pointer' : ((activeTab === 'Editing' || activeTab === 'Translating') ? 'cursor-crosshair' : 'cursor-default')} select-none`}
                draggable={false}
            />
          )}


          {/* Annotation Overlay */}
          {renderedSize && imgRef.current && (
            <AnnotationOverlay
              comments={comments}
              currentPictureId={currentPictureId}
              imageWidth={renderedSize.width}
              imageHeight={renderedSize.height}
              naturalWidth={imgRef.current.naturalWidth}
              naturalHeight={imgRef.current.naturalHeight}
              highlightedCommentId={highlightedCommentId}
              isAnnotationMode={isAnnotationMode}
            />
          )}

          {/* Pending annotation marker */}
          {pendingAnnotation && renderedSize && imgRef.current && (() => {
            const { naturalWidth, naturalHeight } = imgRef.current!;
            const clientWidth = renderedSize.width;
            const clientHeight = renderedSize.height;
            const imgRatio = naturalWidth > 0 && naturalHeight > 0 ? naturalWidth / naturalHeight : clientWidth / clientHeight;
            const elementRatio = clientWidth / clientHeight;
            let paintedWidth: number, paintedHeight: number;
            if (elementRatio > imgRatio) {
              paintedHeight = clientHeight;
              paintedWidth = paintedHeight * imgRatio;
            } else {
              paintedWidth = clientWidth;
              paintedHeight = paintedWidth / imgRatio;
            }
            return (
              <div
                className="absolute pointer-events-none z-20"
                style={{
                  left: `calc(50% - ${paintedWidth / 2}px + ${pendingAnnotation.x * paintedWidth}px - 12px)`,
                  top: `calc(50% - ${paintedHeight / 2}px + ${pendingAnnotation.y * paintedHeight}px - 12px)`,
                }}
              >
                <div className="w-6 h-6 rounded-full bg-orange-500 border-2 border-white" />
                <div className="absolute inset-0 w-6 h-6 rounded-full animate-ping bg-orange-400 opacity-75" />
              </div>
            );
          })()}

          {/* Hotspot indicator */}
          {editHotspot && !isLoading && activeTab === 'Editing' && imgSize && renderedSize && (
            (() => {
               const clientWidth = renderedSize.width;
               const clientHeight = renderedSize.height;

               // Calculate the actual painted image size (accounting for object-contain)
               const imgRatio = imgSize.width / imgSize.height;
               const elementRatio = clientWidth / clientHeight;

               let paintedWidth, paintedHeight;
               if (elementRatio > imgRatio) {
                  paintedHeight = clientHeight;
                  paintedWidth = paintedHeight * imgRatio;
               } else {
                  paintedWidth = clientWidth;
                  paintedHeight = paintedWidth / imgRatio;
               }

               // Calculate hotspot position on the painted image
               const hotspotX = (editHotspot.x / imgSize.width) * paintedWidth;
               const hotspotY = (editHotspot.y / imgSize.height) * paintedHeight;

               return (
                <div
                    className="absolute rounded-full w-6 h-6 bg-blue-500/50 border-2 border-white pointer-events-none z-10"
                    style={{
                      left: `calc(50% - ${paintedWidth / 2}px + ${hotspotX}px - 12px)`,
                      top: `calc(50% - ${paintedHeight / 2}px + ${hotspotY}px - 12px)`
                    }}
                >
                    <div className="absolute inset-0 rounded-full w-6 h-6 animate-ping bg-blue-400"></div>
                </div>
               );
            })()
          )}
        </>
      ) : currentImage ? (
        /* Single image display (original behavior for groups with only 1 image) */
        <>
          {/* Star button */}
          <button
            onClick={() => handleToggleStar(currentImage)}
            className="absolute top-4 left-4 z-20 p-1.5 rounded-full bg-black/50 backdrop-blur-sm transition-colors hover:bg-black/70"
            aria-label={currentImage.starred ? 'Unstar image' : 'Star image'}
            title={currentImage.starred ? 'Remove star' : 'Star this image'}
          >
            <StarIcon
              className={`w-5 h-5 ${currentImage.starred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
            />
          </button>

          {/* Base image is the original, always at the bottom */}
          {originalImage && (
              <img
                  key='original-image'
                  src={originalImage.output_url}
                  alt="Original"
                  className={`w-full h-auto object-contain max-h-[calc(100vh-200px)] rounded-xl pointer-events-none transition-opacity duration-200 ease-in-out ${isComparing ? 'opacity-100' : 'opacity-0'}`}
              />
          )}
          {/* The current image — wrapped in ReactCrop when region mode is active */}
          {isRegionSelectMode && (activeTab === 'Editing' || activeTab === 'Translating') ? (
            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-in-out ${isComparing ? 'opacity-0' : 'opacity-100'}`}>
              <ReactCrop
                crop={regionCrop}
                onChange={c => setRegionCrop(c)}
                onComplete={c => setRegionCompletedCrop(c)}
                aspect={regionAspect}
                style={{ maxHeight: 'calc(100vh - 200px)', maxWidth: '100%' }}
              >
                <img
                  ref={setImgRef}
                  key={currentImage.output_url}
                  src={currentImage.output_url}
                  alt="Current"
                  className="block max-w-full max-h-[calc(100vh-200px)] rounded-xl select-none"
                  draggable={false}
                />
              </ReactCrop>
            </div>
          ) : (
            <img
                ref={setImgRef}
                key={currentImage.output_url}
                src={currentImage.output_url}
                alt="Current"
                onMouseDown={handleImageClick}
                className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-auto object-contain max-h-[calc(100vh-200px)] rounded-xl transition-opacity duration-200 ease-in-out ${isComparing ? 'opacity-0' : 'opacity-100'} ${isAnnotationMode ? 'cursor-pointer' : ((activeTab === 'Editing' || activeTab === 'Translating') ? 'cursor-crosshair' : 'cursor-default')}`}
                draggable={false}
            />
          )}


          {/* Annotation Overlay - always visible, but pins are non-interactive in annotation mode */}
          {renderedSize && imgRef.current && (
            <AnnotationOverlay
              comments={comments}
              currentPictureId={currentPictureId}
              imageWidth={renderedSize.width}
              imageHeight={renderedSize.height}
              naturalWidth={imgRef.current.naturalWidth}
              naturalHeight={imgRef.current.naturalHeight}
              highlightedCommentId={highlightedCommentId}
              isAnnotationMode={isAnnotationMode}
            />
          )}

          {/* Pending annotation marker */}
          {pendingAnnotation && renderedSize && imgRef.current && (() => {
            const { naturalWidth, naturalHeight } = imgRef.current!;
            const clientWidth = renderedSize.width;
            const clientHeight = renderedSize.height;
            const imgRatio = naturalWidth > 0 && naturalHeight > 0 ? naturalWidth / naturalHeight : clientWidth / clientHeight;
            const elementRatio = clientWidth / clientHeight;
            let paintedWidth: number, paintedHeight: number;
            if (elementRatio > imgRatio) {
              paintedHeight = clientHeight;
              paintedWidth = paintedHeight * imgRatio;
            } else {
              paintedWidth = clientWidth;
              paintedHeight = paintedWidth / imgRatio;
            }
            return (
              <div
                className="absolute pointer-events-none z-20"
                style={{
                  left: `calc(50% - ${paintedWidth / 2}px + ${pendingAnnotation.x * paintedWidth}px - 12px)`,
                  top: `calc(50% - ${paintedHeight / 2}px + ${pendingAnnotation.y * paintedHeight}px - 12px)`,
                }}
              >
                <div className="w-6 h-6 rounded-full bg-orange-500 border-2 border-white" />
                <div className="absolute inset-0 w-6 h-6 rounded-full animate-ping bg-orange-400 opacity-75" />
              </div>
            );
          })()}

          {/* Hotspot indicator - positioned relative to the actual image */}
          {editHotspot && !isLoading && activeTab === 'Editing' && imgSize && renderedSize && (
            (() => {
               const clientWidth = renderedSize.width;
               const clientHeight = renderedSize.height;

               // Calculate the actual painted image size (accounting for object-contain)
               const imgRatio = imgSize.width / imgSize.height;
               const elementRatio = clientWidth / clientHeight;

               let paintedWidth, paintedHeight;
               if (elementRatio > imgRatio) {
                  paintedHeight = clientHeight;
                  paintedWidth = paintedHeight * imgRatio;
               } else {
                  paintedWidth = clientWidth;
                  paintedHeight = paintedWidth / imgRatio;
               }

               // Calculate hotspot position on the painted image
               const hotspotX = (editHotspot.x / imgSize.width) * paintedWidth;
               const hotspotY = (editHotspot.y / imgSize.height) * paintedHeight;

               return (
                <div
                    className="absolute rounded-full w-6 h-6 bg-blue-500/50 border-2 border-white pointer-events-none z-10"
                    style={{
                      left: `calc(50% - ${paintedWidth / 2}px + ${hotspotX}px - 12px)`,
                      top: `calc(50% - ${paintedHeight / 2}px + ${hotspotY}px - 12px)`
                    }}
                >
                    <div className="absolute inset-0 rounded-full w-6 h-6 animate-ping bg-blue-400"></div>
                </div>
               );
            })()
          )}
        </>
      ) : null}
      {activeTab === 'Reformat' && hoveredCombo && currentImage && (
        <SafeZoneOverlay combo={hoveredCombo} imgRef={imgRef} />
      )}
    </div>
  );

  // For ReactCrop, we need a single image element. We'll use the current one.
  // Only render crop element when an image is selected
  const cropImageElement = currentImage ? (
    <img
      ref={setImgRef}
      key={`crop-${currentImage.output_url}`}
      src={currentImage.output_url}
      alt="Crop Target"
      className="w-full h-auto object-contain rounded-xl"
      style={{ maxHeight: 'calc(100vh - 200px)' }}
    />
  ) : null;

  return (
    <div className="relative flex-1 shadow-2xl rounded-xl overflow-hidden bg-black/20">
      {(isLoading || (imagesLoading && currentImage)) && (
          <div className="absolute inset-0 bg-black/70 z-30 flex flex-col items-center justify-center gap-4 animate-fade-in">
              <Spinner />
              <p className="text-gray-300 text-sm">{loadingMessage || (isLoading ? 'AI is working its magic...' : 'Loading images...')}</p>
          </div>
      )}

      {activeTab === 'Cropping' ? (
        <div className="w-full h-full flex items-center justify-center relative">
          {/* Show close button when an image from a multi-image group is selected */}
          {currentImageGroup && currentImageGroup.images.length > 1 && selectedImageInGroup >= 0 && currentImage && (
            <button
              onClick={() => setSelectedImageInGroup(-1)}
              className="absolute top-4 right-4 z-30 p-2 bg-red-500/90 hover:bg-red-600 backdrop-blur-sm text-white rounded-full transition-all duration-200 hover:scale-110 active:scale-95 shadow-lg group"
              aria-label="Return to grid view"
            >
              <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
            </button>
          )}

          {currentImage ? (
            <ReactCrop
              crop={crop}
              onChange={c => setCrop(c)}
              onComplete={c => setCompletedCrop(c)}
              aspect={aspect}
              className="max-h-full"
            >
              {cropImageElement}
            </ReactCrop>
          ) : (
            /* Show image grid when no image is selected in cropping mode */
            imageDisplay
          )}
        </div>
      ) : imageDisplay }
    </div>
  );
};

export default EditorCanvas;
